"use client";

import * as React from "react";
import { useRef, useEffect } from "react";
import { ArrowUp, Square } from "lucide-react";
import { Button } from "../ui/button";
import { useAutoResizeTextarea } from "../../hooks/use-auto-resize-textarea";
import { cn } from "../../lib/utils";

interface ChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  isLoading: boolean;
  greeting?: boolean;
  /** Fine print below the composer. Absent ⇒ no paragraph is rendered. */
  disclaimer?: React.ReactNode;
}

export function ChatInput({ input, onInputChange, onSend, onStop, isLoading, greeting, disclaimer }: ChatInputProps) {
  const { textareaRef, adjustHeight } = useAutoResizeTextarea(200);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        onSend();
        adjustHeight(true);
      }
    }
    if (e.key === "Escape" && isLoading) {
      onStop();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSend();
      adjustHeight(true);
    }
  };

  return (
    <div className={cn(greeting ? "w-full" : "border-t bg-background px-4 pt-3.5 pb-3")}>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn(
          "relative mx-auto",
          greeting ? "max-w-2xl" : "max-w-3xl"
        )}
      >
        {/* The height floor is required, not decorative: the auto-resize hook assigns
            min(scrollHeight, 200) straight to style.height, and scrollHeight on a border-box element
            excludes the 2px border — so without a CSS floor the field settles at 42px and the send
            control it centres sits one grid step off. A min-height still wins over an inline height,
            so the floor is a class, not a change to the hook or its 200px cap. */}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything"
          rows={1}
          className="block w-full min-h-[44px] resize-none overflow-hidden rounded-xl border border-input bg-muted px-4 py-[11px] pr-14 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          aria-label="Chat message input"
        />
        <div className="absolute right-2 bottom-1.5 flex items-center">
          {isLoading ? (
            <Button
              type="button"
              size="icon"
              variant="destructive"
              className="h-8 w-8 rounded-full"
              onClick={onStop}
              aria-label="Stop generating"
            >
              <Square className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              className="h-8 w-8 rounded-full"
              disabled={!input.trim()}
              aria-label="Send message"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </form>
      {!greeting && disclaimer ? (
        <p className="text-center text-xs text-muted-foreground mt-2">{disclaimer}</p>
      ) : null}
    </div>
  );
}
